import {
  fileEntrySchema,
  hasPermission,
  signedUrlSchema,
  type ClientServer,
  type FileEntry,
  type Permission,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Copy,
  Download,
  File,
  FilePlus,
  FileSymlink,
  Folder,
  FolderInput,
  FolderPlus,
  Home,
  Lock,
  MoreHorizontal,
  PackageOpen,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ChmodDialog, TextDialog } from '@/components/files/dialogs';
import { UploadPanel } from '@/components/files/upload-panel';
import { QueryState } from '@/components/query-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { apiRequest } from '@/lib/api';
import { bytes, dateTime, timeAgo } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import {
  commonEntries,
  crumbs,
  fileShortcut,
  isArchive,
  isEditable,
  joinPath,
  normalizeDir,
  parentOf,
} from '@/lib/files';
import { serverQuery } from '@/lib/server-queries';
import { uploads } from '@/lib/uploads';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/server/$server/files')({
  validateSearch: (s: Record<string, unknown>): { dir?: string } =>
    typeof s.dir === 'string' && s.dir !== '/' ? { dir: normalizeDir(s.dir) } : {},
  component: FilesPage,
});

type SortKey = 'name' | 'size' | 'modified';
type DialogState =
  | { kind: 'new-folder' }
  | { kind: 'new-file' }
  | { kind: 'rename'; entry: FileEntry }
  | { kind: 'move'; names: string[] }
  | { kind: 'chmod'; names: string[]; initial: string }
  | { kind: 'delete'; names: string[] };

interface Action {
  label: string;
  icon: typeof File;
  run: () => void;
  destructive?: boolean;
  separatorBefore?: boolean;
}

function FilesPage() {
  const { server: id } = Route.useParams();
  const { dir: rawDir } = Route.useSearch();
  const dir = normalizeDir(rawDir ?? '/');
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const can = (p: Permission) => hasPermission(server.permissions, p);

  const listKey = ['server', id, 'files', dir];
  const list = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${id}/files/list?directory=${encodeURIComponent(dir)}`, {
        schema: z.object({ data: z.array(fileEntrySchema) }),
        signal,
      }).then((r) => r.data),
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'name', desc: false });
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['server', id, 'files'] });
  const go = (to: string) => {
    setSelected(new Set());
    void navigate({ search: to === '/' ? {} : { dir: to } });
  };

  const op = useMutation({
    mutationFn: async ({
      path,
      method = 'POST',
      body,
    }: {
      path: string;
      method?: 'POST' | 'PUT';
      body: unknown;
    }) =>
      apiRequest(`/api/client/servers/${id}/files/${path}`, { method, body, schema: z.unknown() }),
    onSuccess: async () => {
      setDialog(null);
      setSelected(new Set());
      await refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const run = (path: string, body: unknown, success: string, method?: 'POST' | 'PUT') =>
    op.mutate({ path, body, method }, { onSuccess: () => toast.success(success) });

  const download = async (name: string) => {
    try {
      const { url } = await apiRequest(
        `/api/client/servers/${id}/files/download?file=${encodeURIComponent(joinPath(dir, name))}`,
        { schema: signedUrlSchema },
      );
      window.location.assign(url);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const startUpload = (files: File[]) => {
    if (!files.length) return;
    if (!can('file.create')) {
      toast.error('You can’t upload files to this server.');
      return;
    }
    uploads.add(id, dir, files, () => void refresh());
  };

  const entries = useMemo(() => {
    const rows = [...(list.data ?? [])];
    const factor = sort.desc ? -1 : 1;
    rows.sort((a, b) => {
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
      const cmp =
        sort.key === 'size'
          ? a.size - b.size
          : sort.key === 'modified'
            ? a.modifiedAt.localeCompare(b.modifiedAt)
            : a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
      return cmp * factor;
    });
    return rows;
  }, [list.data, sort]);

  const common = useMemo(() => commonEntries(entries), [entries]);

  const open = (e: FileEntry) => {
    if (e.isDirectory) go(joinPath(dir, e.name));
    else if (isEditable(e) && can('file.read-content'))
      void navigate({
        to: '/server/$server/edit',
        params: { server: id },
        search: { file: joinPath(dir, e.name) },
      });
  };

  // Keyboard shortcuts (rules in lib/files.ts). The handler is re-bound each render so it
  // always sees the current selection and folder.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      const busy =
        dialog !== null ||
        !!t?.closest('input, textarea, select, [contenteditable="true"], [role="menu"]') ||
        !!document.querySelector('[role="menu"], [role="dialog"], [role="alertdialog"]');
      const one = selected.size === 1 ? entries.find((e) => selected.has(e.name)) : undefined;
      const action = fileShortcut(ev, {
        busy,
        selected: selected.size,
        entries: entries.length,
        atRoot: dir === '/',
        canDelete: can('file.delete'),
        canUpdate: can('file.update') && !one?.denied,
      });
      if (!action) return;
      ev.preventDefault();
      if (action === 'select-all') setSelected(new Set(entries.map((e) => e.name)));
      else if (action === 'clear') setSelected(new Set());
      else if (action === 'delete') setDialog({ kind: 'delete', names: [...selected] });
      else if (action === 'rename' && one) setDialog({ kind: 'rename', entry: one });
      else if (action === 'open' && one) open(one);
      else if (action === 'up') go(parentOf(dir));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const actionsFor = (e: FileEntry): Action[] => {
    const out: Action[] = [];
    const name = e.name;
    if (e.isDirectory) out.push({ label: 'Open', icon: Folder, run: () => open(e) });
    else if (isEditable(e) && can('file.read-content'))
      out.push({ label: 'Edit', icon: Pencil, run: () => open(e) });
    if (e.denied) return out;
    if (can('file.update')) {
      out.push({
        label: 'Rename',
        icon: Pencil,
        run: () => setDialog({ kind: 'rename', entry: e }),
      });
      out.push({
        label: 'Move',
        icon: FolderInput,
        run: () => setDialog({ kind: 'move', names: [name] }),
      });
    }
    if (e.isFile && can('file.create'))
      out.push({
        label: 'Copy',
        icon: Copy,
        run: () => run('copy', { location: joinPath(dir, name) }, `Copied ${name}.`),
      });
    if (e.isFile && can('file.read-content'))
      out.push({ label: 'Download', icon: Download, run: () => void download(name) });
    if (can('file.update') && !e.isSymlink)
      out.push({
        label: 'Permissions',
        icon: ShieldCheck,
        run: () => setDialog({ kind: 'chmod', names: [name], initial: e.modeBits }),
      });
    if (can('file.archive')) {
      out.push({
        label: 'Archive',
        icon: Archive,
        separatorBefore: true,
        run: () => run('compress', { root: dir, files: [name] }, 'Archive created.'),
      });
      if (e.isFile && isArchive(name))
        out.push({
          label: 'Extract here',
          icon: PackageOpen,
          run: () => run('decompress', { root: dir, file: name }, `Extracted ${name}.`),
        });
    }
    if (can('file.delete'))
      out.push({
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        separatorBefore: true,
        run: () => setDialog({ kind: 'delete', names: [name] }),
      });
    return out;
  };

  const selectedNames = [...selected];
  const allSelected = entries.length > 0 && entries.every((e) => selected.has(e.name));
  const toggle = (name: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    startUpload([...e.dataTransfer.files]);
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Folder" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => go('/')}
            className="hover:bg-accent flex items-center gap-1 rounded px-1.5 py-1 font-mono"
            aria-label="Server root"
          >
            <Home className="size-4" /> /home/container
          </button>
          {crumbs(dir).map((c) => (
            <span key={c.path} className="flex items-center gap-1">
              <ChevronRight className="text-muted-foreground size-3.5" />
              <button
                type="button"
                onClick={() => go(c.path)}
                className="hover:bg-accent max-w-48 truncate rounded px-1.5 py-1 font-mono"
              >
                {c.name}
              </button>
            </span>
          ))}
        </nav>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => void refresh()} aria-label="Refresh">
            <RefreshCw className={cn(list.isFetching && 'animate-spin')} />
          </Button>
          {can('file.create') && (
            <>
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'new-folder' })}>
                <FolderPlus /> New folder
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'new-file' })}>
                <FilePlus /> New file
              </Button>
              <Button size="sm" onClick={() => fileInput.current?.click()}>
                <Upload /> Upload
              </Button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  startUpload([...(e.target.files ?? [])]);
                  e.target.value = '';
                }}
              />
            </>
          )}
        </div>
      </div>

      {dir === '/' && common.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Common files">
          <span className="text-muted-foreground text-sm">Jump to</span>
          {common.map((e) => (
            <Button key={e.name} size="sm" variant="outline" onClick={() => open(e)}>
              {e.isDirectory ? <Folder /> : <File />}
              <span className="font-mono">{e.name}</span>
            </Button>
          ))}
        </div>
      )}

      <UploadPanel server={id} />

      {selected.size > 0 && (
        <div className="bg-primary/5 border-primary/30 animate-in fade-in-0 slide-in-from-top-1 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm duration-150">
          <span className="mr-auto">{selected.size} selected</span>
          {can('file.update') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDialog({ kind: 'move', names: selectedNames })}
            >
              <FolderInput /> Move
            </Button>
          )}
          {can('file.archive') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <Archive /> Archive
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {(['tar.gz', 'zip'] as const).map((format) => (
                  <DropdownMenuItem
                    key={format}
                    onSelect={() =>
                      run(
                        'compress',
                        { root: dir, files: selectedNames, format },
                        'Archive created.',
                      )
                    }
                  >
                    As .{format}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {can('file.delete') && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setDialog({ kind: 'delete', names: selectedNames })}
            >
              <Trash2 /> Delete
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <Card
        className={cn(
          'gap-0 overflow-hidden p-0 transition-colors',
          dragging && 'border-primary bg-primary/5',
        )}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
        }}
        onDrop={onDrop}
      >
        <QueryState query={list}>
          {() => (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground bg-elevated/60 border-b text-left text-xs">
                <tr>
                  <th className="w-10 py-2 pl-4">
                    <Checkbox
                      aria-label="Select all"
                      checked={allSelected ? true : selected.size > 0 ? 'indeterminate' : false}
                      onCheckedChange={(v) =>
                        setSelected(v === true ? new Set(entries.map((e) => e.name)) : new Set())
                      }
                    />
                  </th>
                  <SortHeader label="Name" k="name" sort={sort} setSort={setSort} />
                  <SortHeader
                    label="Size"
                    k="size"
                    sort={sort}
                    setSort={setSort}
                    className="hidden w-28 text-right sm:table-cell"
                  />
                  <SortHeader
                    label="Modified"
                    k="modified"
                    sort={sort}
                    setSort={setSort}
                    className="hidden w-40 text-right md:table-cell"
                  />
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {dir !== '/' && (
                  <tr className="hover:bg-elevated/50 border-b">
                    <td />
                    <td colSpan={4} className="py-2">
                      <button
                        type="button"
                        onClick={() => go(parentOf(dir))}
                        className="flex items-center gap-2 font-mono"
                      >
                        <Folder className="text-muted-foreground size-4" /> ..
                      </button>
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <FileRow
                    key={e.name}
                    entry={e}
                    selected={selected.has(e.name)}
                    onToggle={() => toggle(e.name)}
                    onOpen={() => open(e)}
                    actions={actionsFor(e)}
                  />
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-muted-foreground py-12 text-center">
                      This folder is empty.
                      {can('file.create') && ' Drop files here to upload them.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </QueryState>
      </Card>
      <p className="text-muted-foreground hidden text-xs md:block">
        Shortcuts: <Kbd>⌘A</Kbd> select all · <Kbd>Enter</Kbd> open · <Kbd>F2</Kbd> rename ·{' '}
        <Kbd>Delete</Kbd> delete · <Kbd>Alt ↑</Kbd> parent folder · <Kbd>Esc</Kbd> clear selection
      </p>

      <SftpHint server={server} />

      {dialog?.kind === 'new-folder' && (
        <TextDialog
          title="New folder"
          label="Name"
          initial=""
          action="Create"
          pending={op.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(name) => run('create-folder', { root: dir, name }, `Created ${name}.`)}
        />
      )}
      {dialog?.kind === 'new-file' && (
        <TextDialog
          title="New file"
          label="Name"
          initial=""
          action="Create and edit"
          pending={false}
          onClose={() => setDialog(null)}
          onSubmit={(name) =>
            void navigate({
              to: '/server/$server/edit',
              params: { server: id },
              search: { file: joinPath(dir, name), new: true },
            })
          }
        />
      )}
      {dialog?.kind === 'rename' && (
        <TextDialog
          title={`Rename ${dialog.entry.name}`}
          label="New name"
          initial={dialog.entry.name}
          action="Rename"
          pending={op.isPending}
          validate={(v) => (v.includes('/') ? 'Use Move to put it in another folder.' : null)}
          onClose={() => setDialog(null)}
          onSubmit={(to) =>
            run(
              'rename',
              { root: dir, files: [{ from: dialog.entry.name, to }] },
              'Renamed.',
              'PUT',
            )
          }
        />
      )}
      {dialog?.kind === 'move' && (
        <TextDialog
          title={
            dialog.names.length === 1
              ? `Move ${dialog.names[0]}`
              : `Move ${dialog.names.length} items`
          }
          description="The folder to move into, from the server root. Missing folders are created."
          label="Destination folder"
          initial={dir}
          action="Move"
          pending={op.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(target) =>
            run(
              'rename',
              {
                root: '/',
                files: dialog.names.map((n) => ({
                  from: joinPath(dir, n),
                  to: joinPath(target, n),
                })),
              },
              'Moved.',
              'PUT',
            )
          }
        />
      )}
      {dialog?.kind === 'chmod' && (
        <ChmodDialog
          names={dialog.names}
          initial={dialog.initial}
          pending={op.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(mode) =>
            run(
              'chmod',
              { root: dir, files: dialog.names.map((file) => ({ file, mode })) },
              'Permissions updated.',
            )
          }
        />
      )}
      <AlertDialog open={dialog?.kind === 'delete'} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialog?.kind === 'delete' && dialog.names.length === 1
                ? `Delete ${dialog.names[0]}?`
                : `Delete ${dialog?.kind === 'delete' ? dialog.names.length : 0} items?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Folders are deleted with everything inside them. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={() =>
                dialog?.kind === 'delete' &&
                run('delete', { root: dir, files: dialog.names }, 'Deleted.')
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SortHeader({
  label,
  k,
  sort,
  setSort,
  className,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; desc: boolean };
  setSort: (s: { key: SortKey; desc: boolean }) => void;
  className?: string;
}) {
  const active = sort.key === k;
  return (
    <th
      className={cn('py-2 pr-4 font-medium', className)}
      aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className={cn(
          'hover:text-foreground inline-flex items-center gap-1',
          active && 'text-foreground',
        )}
        onClick={() => setSort({ key: k, desc: active ? !sort.desc : false })}
      >
        {label}
        {active && (sort.desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
      </button>
    </th>
  );
}

function MenuItems({ actions, kind }: { actions: Action[]; kind: 'context' | 'dropdown' }) {
  const Item = kind === 'context' ? ContextMenuItem : DropdownMenuItem;
  const Sep = kind === 'context' ? ContextMenuSeparator : DropdownMenuSeparator;
  const out: ReactNode[] = [];
  actions.forEach((a, i) => {
    if (a.separatorBefore && i > 0) out.push(<Sep key={`sep-${a.label}`} />);
    out.push(
      <Item key={a.label} variant={a.destructive ? 'destructive' : 'default'} onSelect={a.run}>
        <a.icon /> {a.label}
      </Item>,
    );
  });
  return <>{out}</>;
}

function FileRow({
  entry: e,
  selected,
  onToggle,
  onOpen,
  actions,
}: {
  entry: FileEntry;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  actions: Action[];
}) {
  const Icon = e.isDirectory
    ? Folder
    : e.isSymlink
      ? FileSymlink
      : isArchive(e.name)
        ? Archive
        : File;
  const clickable = e.isDirectory || isEditable(e);
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <tr
          className={cn(
            'hover:bg-elevated/50 border-b transition-colors last:border-0',
            selected && 'bg-primary/[0.06]',
          )}
        >
          <td className="py-2.5 pl-4">
            <Checkbox
              aria-label={`Select ${e.name}`}
              checked={selected}
              onCheckedChange={onToggle}
            />
          </td>
          <td className="max-w-0 py-2.5 pr-4">
            <button
              type="button"
              onClick={onOpen}
              disabled={!clickable}
              className={cn(
                'flex max-w-full items-center gap-2 text-left',
                clickable ? 'hover:underline' : 'cursor-default',
              )}
              title={e.name}
            >
              <Icon
                className={cn(
                  'size-4 shrink-0',
                  e.isDirectory ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              <span className="truncate">{e.name}</span>
              {e.denied && (
                <Lock
                  className="text-muted-foreground size-3.5 shrink-0"
                  aria-label="Protected by the server's egg"
                />
              )}
            </button>
          </td>
          <td className="text-muted-foreground hidden py-2 pr-4 text-right tabular-nums sm:table-cell">
            {e.isFile ? bytes(e.size) : ''}
          </td>
          <td
            className="text-muted-foreground hidden py-2 pr-4 text-right md:table-cell"
            title={dateTime(e.modifiedAt)}
          >
            {timeAgo(e.modifiedAt)}
          </td>
          <td className="py-1 pr-2 text-right">
            {actions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label={`Actions for ${e.name}`}
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <MenuItems actions={actions} kind="dropdown" />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </td>
        </tr>
      </ContextMenuTrigger>
      {actions.length > 0 && (
        <ContextMenuContent>
          <MenuItems actions={actions} kind="context" />
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}

function SftpHint({ server }: { server: ClientServer }) {
  if (!hasPermission(server.permissions, 'file.sftp')) return null;
  const { host, port, username } = server.sftp;
  return (
    <p className="text-muted-foreground text-xs">
      SFTP:{' '}
      <code className="text-foreground">
        sftp -P {port} {username}@{host}
      </code>{' '}
      with your panel password or an{' '}
      <Link to="/account/ssh-keys" className="underline">
        SSH key
      </Link>
      .
    </p>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="bg-muted text-foreground rounded border px-1 py-px font-mono text-[11px]">
      {children}
    </kbd>
  );
}
